import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Copy, Download, LockKeyhole, Share } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Casilla } from '@/components/Casilla';
import { PasswordField } from '@/components/Field';
import { Segmentado } from '@/components/Segmentado';
import { prepararRespaldo, prepararRespaldoConFotos } from '@/db/backup';
import { registrarRespaldoHecho } from '@/db/backup';
import { cifrarZip } from '@/db/backup-zip';
import { cifrarRespaldo } from '@/db/backup-crypto';
import { espacioDeFotos } from '@/db/fotos';
import { mensajeDeError } from '@/db/tx';
import type { Settings } from '@/db/types';
import { formatearBytes } from '@/lib/storage';
import {
  compartirBlob,
  copiarAlPortapapeles,
  descargarBlob,
  puedeCompartirArchivos,
} from '@/lib/file-transfer';

interface Listo {
  nombre: string;
  /** Lo que se entrega, tal cual: el JSON o el ZIP. */
  blob: Blob;
  /**
   * El contenido como texto, cuando se puede copiar o mostrar. Un ZIP no:
   * son bytes y pegarlos en una nota no serviría de nada.
   */
  texto: string | null;
  totalRegistros: number;
  /** Cuántas fotos trae, o null si es el respaldo sin fotos. */
  fotos: number | null;
  cifrado: boolean;
  bytes: number;
  /** Instante de los datos del archivo: es lo que se registra como último respaldo. */
  exportadoEn: string;
  /** Datos raros encontrados al verificar; no impiden entregarlo. */
  avisos: string[];
}

const comoJson = (texto: string) => new Blob([texto], { type: 'application/json;charset=utf-8' });

type Aviso = { tono: 'exito' | 'aviso' | 'alerta'; titulo: string; texto: string } | null;

type Que = 'sin_fotos' | 'con_fotos';

/**
 * Hacer un respaldo (Req. 6.1 a 6.4, 6.13, 6.17, 6.20, 6.21).
 *
 * En dos pasos a propósito: primero se genera y se VERIFICA el archivo
 * (y se cifra si toca); después se entrega. En iPhone, compartir y copiar al
 * portapapeles solo funcionan si se llaman justo al tocar un botón: si antes
 * hubiera que esperar a generar o cifrar, Safari los rechazaría.
 *
 * Con fotos el archivo es un ZIP que lleva el mismo respaldo adentro, más una
 * imagen por foto (design.md 10.7). La diferencia se dice en pantalla: el
 * respaldo sin fotos NO las incluye, y hay que saberlo antes de guardarlo
 * como el único respaldo.
 */
export function HacerRespaldo({ settings }: { settings: Settings }) {
  const cifradoActivo = settings.cifradoRespaldo !== null;
  const guardadas = useLiveQuery(() => espacioDeFotos(), []);
  const [que, setQue] = useState<Que>('con_fotos');
  const [contrasena, setContrasena] = useState('');
  const [sinCifrar, setSinCifrar] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [listo, setListo] = useState<Listo | null>(null);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [textoVisible, setTextoVisible] = useState(false);
  /** La descarga no avisa si se guardó: se pregunta antes de registrarla (Req. 6.4). */
  const [preguntandoDescarga, setPreguntandoDescarga] = useState(false);
  const puedeCompartir = puedeCompartirArchivos();

  const cifrar = cifradoActivo && !sinCifrar;
  // Sin fotos guardadas no hay nada que elegir: es el respaldo de siempre
  const cuantas = guardadas?.fotos ?? 0;
  const hayFotos = cuantas > 0;
  const conFotos = hayFotos && que === 'con_fotos';
  const cuantasFotos = cuantas === 1 ? '1 foto de progreso' : `${cuantas} fotos de progreso`;

  async function preparar() {
    setPreparando(true);
    setAviso(null);
    setListo(null);
    setTextoVisible(false);
    setPreguntandoDescarga(false);
    try {
      if (conFotos) {
        const preparado = await prepararRespaldoConFotos();
        const comunes = {
          totalRegistros: preparado.totalRegistros,
          fotos: preparado.fotos,
          exportadoEn: preparado.archivo.exportedAt,
          avisos: preparado.avisos,
        };
        if (cifrar && settings.cifradoRespaldo !== null) {
          const cifrado = await cifrarZip(
            preparado.zip,
            { texto: preparado.texto, archivo: preparado.archivo },
            contrasena,
            settings.cifradoRespaldo,
          );
          const blob = comoJson(cifrado.texto);
          setListo({
            ...comunes,
            nombre: cifrado.nombre,
            blob,
            // Son megabytes de imágenes codificadas: no cabe en una nota
            texto: null,
            cifrado: true,
            bytes: blob.size,
          });
        } else {
          const blob = new Blob([preparado.zip], { type: 'application/zip' });
          setListo({
            ...comunes,
            nombre: preparado.nombre,
            blob,
            texto: null,
            cifrado: false,
            bytes: blob.size,
          });
        }
      } else {
        const preparado = await prepararRespaldo();
        const comunes = {
          totalRegistros: preparado.totalRegistros,
          fotos: null,
          exportadoEn: preparado.archivo.exportedAt,
          avisos: preparado.avisos,
        };
        // Cifrar deriva la clave con 600,000 iteraciones: se hace UNA vez
        const entrega =
          cifrar && settings.cifradoRespaldo !== null
            ? await cifrarRespaldo(preparado, contrasena, settings.cifradoRespaldo)
            : { texto: preparado.texto, nombre: preparado.nombre };
        const blob = comoJson(entrega.texto);
        setListo({
          ...comunes,
          nombre: entrega.nombre,
          blob,
          texto: entrega.texto,
          cifrado: cifrar,
          bytes: blob.size,
        });
      }
      // La contraseña no se queda en memoria más de lo necesario.
      setContrasena('');
    } catch (error) {
      setAviso({ tono: 'alerta', titulo: 'No se generó el respaldo', texto: mensajeDeError(error) });
    } finally {
      setPreparando(false);
    }
  }

  async function marcarHecho(r: Listo, texto: string) {
    setPreguntandoDescarga(false);
    try {
      await registrarRespaldoHecho(r.exportadoEn);
      setAviso({ tono: 'exito', titulo: 'Respaldo guardado', texto });
    } catch (error) {
      setAviso({
        tono: 'aviso',
        titulo: 'El archivo se entregó, pero no se registró la fecha',
        texto: mensajeDeError(error),
      });
    }
  }

  function descargar(r: Listo) {
    descargarBlob(r.nombre, r.blob);
    setAviso(null);
    setPreguntandoDescarga(true);
  }

  async function compartir(r: Listo) {
    setPreguntandoDescarga(false);
    const compartido = await compartirBlob(r.nombre, r.blob, 'Respaldo de DuoFit');
    if (compartido) {
      await marcarHecho(r, `${r.nombre}, compartido desde el menú del iPhone.`);
    } else {
      setAviso({
        tono: 'aviso',
        titulo: 'No se compartió',
        texto:
          'Se cerró el menú o el iPhone no lo permitió. "Guardar en Archivos" es la vía más confiable.',
      });
    }
  }

  async function copiar(r: Listo) {
    if (r.texto === null) return;
    const copiado = await copiarAlPortapapeles(r.texto);
    setTextoVisible(!copiado);
    setAviso({
      tono: 'aviso',
      titulo: copiado ? 'Texto copiado' : 'Copia el texto a mano',
      texto: copiado
        ? 'Pégalo en una nota privada. Copiar no cuenta como respaldo: guárdalo como archivo en cuanto puedas.'
        : 'El iPhone no permitió copiarlo solo. Mantén el dedo sobre el texto de abajo, elige "Seleccionar todo" y cópialo. No cuenta como respaldo.',
    });
  }

  const faltaContrasena = cifrar && contrasena === '';

  return (
    <div className="mt-5 space-y-4">
      {hayFotos && (
        <div className="space-y-2">
          <Segmentado
            etiqueta="Qué incluir en el respaldo"
            etiquetaVisible
            completo
            valor={que}
            opciones={[
              { valor: 'con_fotos', etiqueta: 'Con fotos' },
              { valor: 'sin_fotos', etiqueta: 'Sin fotos' },
            ]}
            onCambio={(v) => {
              setQue(v);
              setListo(null);
              setAviso(null);
            }}
          />
          <p className="text-[13px] leading-relaxed text-texto-suave">
            {conFotos
              ? `Con fotos entrega un archivo .zip con ${cuantasFotos} (${formatearBytes(guardadas?.bytes ?? 0)}) y el mismo respaldo adentro.`
              : `Sin fotos entrega el archivo .json de siempre, más ligero. No lleva ${cuantasFotos}, y restaurarlo tampoco las borra de este iPhone.`}
          </p>
        </div>
      )}

      {cifradoActivo && (
        <div className="space-y-3">
          {!sinCifrar && (
            <PasswordField
              etiqueta="Contraseña de los respaldos"
              valor={contrasena}
              onCambio={setContrasena}
              autoComplete="current-password"
              ayuda="Si la olvidas, no podrás abrir este respaldo."
            />
          )}
          <Casilla marcada={sinCifrar} onCambio={setSinCifrar}>
            Esta vez exportar sin cifrar. Entiendo que el archivo quedará legible para quien lo
            tenga.
          </Casilla>
        </div>
      )}

      <Button
        disabled={preparando || faltaContrasena}
        onClick={() => void preparar()}
        icono={cifrar ? LockKeyhole : Download}
      >
        {preparando
          ? cifrar
            ? 'Cifrando y verificando…'
            : 'Generando y verificando…'
          : cifrar
            ? conFotos
              ? 'Preparar respaldo con fotos cifrado'
              : 'Preparar respaldo cifrado'
            : conFotos
              ? 'Preparar respaldo con fotos'
              : 'Preparar respaldo'}
      </Button>

      {listo !== null && (
        <div className="space-y-3">
          <Banner tono="exito" titulo="Respaldo listo y verificado">
            {listo.nombre} · {listo.totalRegistros} registros
            {listo.fotos === null
              ? ''
              : ` · ${listo.fotos === 1 ? '1 foto' : `${listo.fotos} fotos`}`}{' '}
            · {formatearBytes(listo.bytes)}
            {listo.cifrado ? ' · cifrado' : ''}. Elige cómo guardarlo:
          </Banner>
          {listo.avisos.length > 0 && (
            <Banner tono="aviso" titulo="El respaldo está completo, con estos avisos">
              <ul className="list-disc space-y-1 pl-4">
                {listo.avisos.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </Banner>
          )}
          <Button onClick={() => descargar(listo)} icono={Download}>
            Guardar en Archivos
          </Button>
          {preguntandoDescarga && (
            <Banner tono="info" titulo="¿Quedó guardado el archivo?">
              <p>
                El iPhone no le avisa a la app. Revisa en Archivos → Descargas que esté{' '}
                {listo.nombre}.
              </p>
              <div className="mt-3 space-y-2">
                <Button
                  onClick={() =>
                    void marcarHecho(
                      listo,
                      `${listo.nombre}. Muévelo de Descargas a iCloud Drive o a otro lugar privado.`,
                    )
                  }
                >
                  Sí, quedó guardado
                </Button>
                <Button
                  variante="secundario"
                  onClick={() => {
                    setPreguntandoDescarga(false);
                    setAviso({
                      tono: 'aviso',
                      titulo: 'No se registró el respaldo',
                      texto:
                        'Vuelve a tocar "Guardar en Archivos" y elige "Descargar" en el aviso del iPhone, o usa "Compartir".',
                    });
                  }}
                >
                  No se guardó
                </Button>
              </div>
            </Banner>
          )}
          {puedeCompartir && (
            <Button variante="secundario" onClick={() => void compartir(listo)} icono={Share}>
              Compartir
            </Button>
          )}
          {listo.texto === null ? (
            <p className="text-[13px] leading-relaxed text-texto-suave">
              Un respaldo con fotos no se copia como texto: son imágenes, y no cabrían en una nota.
              Guárdalo en Archivos o compártelo.
            </p>
          ) : (
            <Button variante="fantasma" onClick={() => void copiar(listo)} icono={Copy}>
              Copiar como texto
            </Button>
          )}
        </div>
      )}

      {aviso !== null && (
        <Banner tono={aviso.tono} titulo={aviso.titulo}>
          {aviso.texto}
        </Banner>
      )}

      {listo !== null && listo.texto !== null && textoVisible && (
        <div className="grid gap-[7px]">
          <label htmlFor="texto-respaldo" className="block text-[13px] text-texto-suave">
            Contenido del respaldo
          </label>
          <textarea
            id="texto-respaldo"
            readOnly
            value={listo.texto}
            onFocus={(e) => e.currentTarget.select()}
            className="h-40 w-full rounded-[13px] border border-borde-fuerte bg-superficie p-3 font-mono text-xs text-texto"
          />
        </div>
      )}
    </div>
  );
}
